import { ColumnDef } from '@tanstack/react-table';
import { TFunction } from 'i18next';
import { Trash2 } from 'lucide-react';
import { Control } from 'react-hook-form';
import { NavigateFunction } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { RoleFormData } from '@/modules/settings/types/role';
import { RolesFormData } from '@/modules/settings/validations/role-validation';
import { AuthenticatedRoutes } from '@/router/routes';

interface GetColumnsParams {
  onDelete: (role: RoleFormData, index: number) => void;
  control: Control<RolesFormData>;
  t: TFunction;
  navigate: NavigateFunction;
}

export const getRolesColumns = ({
  onDelete,
  control,
  t,
  navigate,
}: GetColumnsParams): ColumnDef<RoleFormData>[] => [
  {
    accessorKey: 'name',
    header: () => {
      return <div style={{ minWidth: '16rem' }}>{t('pages.roles.nameColumn')}</div>;
    },
    cell: ({ row }) => {
      const index = row.index;

      return (
        <FormField
          control={control}
          name={`roles.${index}.name`}
          render={({ field, fieldState }) => (
            <FormItem className="w-full">
              <FormControl>
                <Input
                  {...field}
                  placeholder={t('pages.roles.namePlaceholder')}
                  className={cn(
                    fieldState.error && 'border-destructive focus-visible:ring-destructive'
                  )}
                />
              </FormControl>
              {fieldState.error && (
                <FormMessage>{t(fieldState.error.message as string)}</FormMessage>
              )}
            </FormItem>
          )}
        />
      );
    },
  },
  {
    accessorKey: 'memberCount',
    header: () => {
      return <div style={{ minWidth: '12rem' }}>{t('pages.roles.membersColumn')}</div>;
    },
    cell: ({ row }) => {
      const role = row.original;
      const hasMembers = role.memberCount > 0;

      const isNewRole = role.roleId < 0;

      return (
        <Button
          type="button"
          variant="ghost"
          disabled={!hasMembers || isNewRole}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            if (hasMembers && !isNewRole) {
              navigate(`${AuthenticatedRoutes.Team}?role=${role.roleId}`);
            }
          }}
          className="h-auto w-full justify-start rounded-lg px-3 py-2"
        >
          {role.memberCount}
        </Button>
      );
    },
  },
  {
    id: 'actions',
    size: 80,
    header: () => {
      return <div className="flex w-full justify-end">{t('pages.roles.actionsColumn')}</div>;
    },
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onDelete(row.original, row.index)}
            className="h-8 w-8"
          >
            <Trash2 className="h-4 w-4 text-muted-foreground" />
          </Button>
        </div>
      );
    },
  },
];
